import { notification } from "antd";
import cuid from "cuid";
import { find, get, isEmpty, map, maxBy } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAdvancedFilters } from "shared/components/AdvancedFilter";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { useScreenActionPermissions } from "shared/hooks/usePermissions";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import {
  CREATE_PROJECT,
  EXPORT_PROJECTS,
  UPDATE_PROJECT,
  UPDATE_PROJECT_LIGHT,
} from "utils/api/graphql/mutations/projects";
import { PROCESS_STATUSES } from "utils/api/graphql/queries/process-statuses";
import { TRACERS } from "utils/api/graphql/queries/tracers";
import {
  ACTION_TYPES,
  CONTENT_TYPES,
  INITIAL_VALUES,
  MODULES,
  PROCESS_TAGS,
  SCREENS,
  SUBMODULES,
  TRACER_TYPES,
} from "utils/constants";
import { ADVANCED_FILTERS } from "utils/constants/filters";
import { downloadDocument } from "utils/helpers/files";
import {
  clearPaginationHistory,
  getPaginationHistory,
} from "utils/helpers/pagination";
import { filterByConditions } from "../Detail";
import View from "./View";

const List = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const [loading, setLoading] = useState(true);
  const [tracer, setTracer] = useState(null);
  const [tracers, setTracers] = useState([]);
  const { user } = useContext(StoreContext);
  const [pagination, setPagination] = useState(
    getPaginationHistory("projectsList"),
  );
  const [filter, setFilter] = useState({
    where: { visible: true },
    withSubscriptions: false,
    withContract: false,
    withProcess: false,
    withProcessStatuses: false,
    withTracers: false,
    withFlows: false,
    exclude: [
      "subscriptions",
      "contract",
      "process",
      "processStatuses",
      "flows",
      "tracers",
      "possibleStatuses",
      "conditions",
      "actions",
      "actionsConditions",
    ],
  });
  const [isAssigning, setIsAssigning] = useState({});
  const actions = useScreenActionPermissions({
    module: MODULES.PROJECT,
    submodule: SUBMODULES.BROKER_LIST,
  });

  const advancedFilterHook = useAdvancedFilters({
    filterType: ADVANCED_FILTERS.PROJECT.savedFilterType,
  });

  const [getProcessStatuses, { data: processStatuses }] =
    useLazyQuery(PROCESS_STATUSES);

  // useQuery(TRACER, {
  //   variables: { where: { type: TRACER_TYPES.MANUAL } },
  //   onCompleted: ({ tracer }) => {
  //     setTracer(tracer);
  //     getProcessStatuses({
  //       fetchPolicy: "no-cache",
  //       variables: {
  //         where: { process: { id: get(tracer, "flow.process.id") } },
  //         isIn: { tag: [PROCESS_TAGS.SIGNED, PROCESS_TAGS.CLOSE] },
  //       },
  //     });
  //     setLoading(false);
  //   },
  // });

  useQuery(TRACERS, {
    fetchPolicy: "no-cache",
    variables: { where: { type: TRACER_TYPES.MANUAL, isExternal: false } },
    onCompleted: ({ tracers: { data } }) => {
      setTracers(omitDeep(data, "__typename"));
      setLoading(false);
    },
  });

  const matchStatusesWithProcess = (statuses) => {
    const result = map(
      statuses.filter(({ status }) => status),
      ({ status: { id: statusId, order } }) => {
        const allProcessStatuses = tracers.flatMap(
          (tracer) =>
            tracer?.flow?.process?.processStatuses
              ?.filter(({ id }) => id === statusId)
              .map((status) => ({
                ...status,
                tracer,
              })) || [],
        );
        const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
          return -Math.abs(candidate.order - order);
        });

        return {
          tracer: matchedStatus?.tracer || null,
          processStatus: matchedStatus || null,
        };
      },
    ).filter(({ tracer }) => tracer !== null);
    return maxBy(result, ({ processStatus: { order } }) => parseInt(order, 10));
  };

  const [updateProject] = useMutation(UPDATE_PROJECT, {});
  const [lightUpdateProject] = useMutation(UPDATE_PROJECT_LIGHT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECTS_LIST,
        awaitRefetchQueries: true,
        variables: {
          ...getPaginationHistory("projectsList"),
          where: { visible: true },
          withSubscriptions: false,
          withContract: false,
          withProcess: false,
          withProcessStatuses: false,
          withTracers: false,
          withFlows: false,
          exclude: [
            "subscriptions",
            "contract",
            "process",
            "processStatuses",
            "flows",
            "tracers",
            "possibleStatuses",
            "conditions",
            "actions",
            "actionsConditions",
          ],
        },
      },
    ],
  });

  const [duplicateProject] = useMutation(graphql.mutations.DUPLICATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECTS_LIST,
        awaitRefetchQueries: true,
        variables: {
          where: {
            visible: true,
          },
          ...INITIAL_VALUES,
          ...filter,
        },
      },
    ],
    onCompleted: ({ duplicateProject }) => {
      clearPaginationHistory("projects");
      navigate(
        `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${duplicateProject.id}`,
        {
          state: {
            process: get(tracer, "flow.process"),
            tracer,
          },
        },
      );
    },
  });

  const [exportProjects, { loading: exportLoading }] =
    useMutation(EXPORT_PROJECTS);
  const [sendProjectsByEmail, { loading: sendByEmailLoading }] = useMutation(
    graphql.mutations.SEND_PROJECTS_BY_EMAIL,
  );
  const [showExportModal, setShowExportModal] = useState(false);

  const handleExportProjects = async (variables = { where: {} }) => {
    try {
      const { data } = await exportProjects({ variables });
      if (data?.exportProjects) {
        downloadDocument({
          base64: data.exportProjects,
          contentType: CONTENT_TYPES.XLSX,
          name: "projects.xlsx",
        });
      }
    } catch (error) {
      console.error("Error exporting projects:", error);
    }
  };

  const handleSendProjectsByEmail = async (variables = { where: {} }) => {
    try {
      // Afficher une notification indiquant que l'email sera envoyé
      notification.info({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_TITLE"),
        description: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_MESSAGE", {
          email: user?.email || "",
        }),
        duration: 10,
      });

      const { data } = await sendProjectsByEmail({ variables });
      if (data?.sendProjectsByEmail !== undefined) {
        // Success - email sent
        notification.success({
          message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_TITLE"),
          description: t(
            "LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_MESSAGE",
            {
              count: data.sendProjectsByEmail,
            },
          ),
          duration: 10,
        });
      }
    } catch (error) {
      console.error("Error sending projects by email:", error);
      notification.error({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_ERROR_TITLE"),
        description: error.message,
        duration: 10,
      });
    }
  };

  const onDetail = (project) => {
    const { tracer } = matchStatusesWithProcess(get(project, "statuses", []));
    navigate(`${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${project.id}`, {
      state: {
        process: get(tracer, "flow.process"),
        tracer,
      },
    });
  };

  const onDelete = (id) =>
    navigate(`${SCREENS.PRIVATE.BROKER.PROJECTS.DELETE.path}/${id}`);

  const onSearch = ({
    clientFirstName = "",
    clientLastName = "",
    advisorFirstName = "",
    advisorLastName = "",
  }) => {
    setPagination((prev) => ({ ...prev, skip: 0 }));
    setFilter((prev) => {
      return {
        ...prev,
        like: {
          ...prev.like,
          AND: [
            {
              contact: {
                user: {
                  firstname: `%${clientFirstName}%`,
                },
              },
            },
            {
              contact: {
                user: {
                  lastname: `%${clientLastName}%`,
                },
              },
            },
            ...(advisorFirstName
              ? [{ author: { firstname: `%${advisorFirstName}%` } }]
              : []),
            ...(advisorLastName
              ? [{ author: { lastname: `%${advisorLastName}%` } }]
              : []),
          ],
        },
      };
    });
  };

  const applyFilter = ({ expired, tags = [], offers = [] }) => {
    const tagFilter = [
      ...(expired
        ? [
            {
              AND: [
                { expired },
                {
                  OR: [
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.CURRENT_SUBSCRIPTION },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.IN_PROGRESS },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.QUOTE_SENT },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.SIGNED },
                      },
                    },
                    {
                      statuses: {
                        status: {
                          tag: PROCESS_TAGS.ELECTRONIC_SIGNATURE_PENDING,
                        },
                      },
                    },
                  ],
                },
              ],
            },
          ]
        : []),
      ...tags?.map((tag) => ({
        AND: [
          { statuses: { status: { tag } } },
          ...(tag === PROCESS_TAGS.CLOSE ? [] : [{ expired: false }]),
        ],
      })),
    ];
    setPagination((prev) => ({ ...prev, skip: 0 }));
    setFilter((prev) => ({
      ...(isEmpty(offers) && !offers.length
        ? {
            ...prev,
            withSubscriptions: false,
            withContract: false,
            withProcess: false,
            withProcessStatuses: false,
            withTracers: false,
            withFlows: false,
            exclude: [
              "subscriptions",
              "contract",
              "process",
              "processStatuses",
              "flows",
              "tracers",
              "possibleStatuses",
              "conditions",
              "actions",
              "actionsConditions",
            ],
          }
        : {
            ...prev,
            withSubscriptions: false,
            withContract: false,
            withProcess: true,
            withTracers: true,
            withFlows: true,
            withProcessStatuses: false,
            exclude: [
              "subscriptions",
              "contract",
              // "process",
              "processStatuses",
              // "flows",
              // "tracers",
              "possibleStatuses",
              "conditions",
              "actions",
              "actionsConditions",
            ],
          }),
      where: {
        ...prev.where,
        AND: [
          ...(!isEmpty(tagFilter)
            ? [
                {
                  OR: tagFilter,
                },
              ]
            : []),
          ...(!isEmpty(offers)
            ? [
                {
                  OR: offers?.map((offer) => ({
                    statuses: {
                      status: {
                        process: {
                          flows: {
                            tracers: {
                              id: get(
                                find(tracers, ({ name }) => name === offer),
                                "id",
                              ),
                            },
                          },
                        },
                      },
                    },
                  })),
                },
              ]
            : []),
        ],
      },
    }));
  };

  const [addProject] = useMutation(CREATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECTS_LIST,
        awaitRefetchQueries: true,
        variables: { where: { visible: true }, ...filter, ...INITIAL_VALUES },
      },
    ],
    onCompleted: ({ addProject }) => {
      clearPaginationHistory("projects");
      navigate(
        `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${addProject.id}`,
        {
          state: {
            process: get(tracer, "flow.process"),
            tracer,
          },
        },
      );
    },
  });

  const createProject = (tracer) => {
    setTracer(tracer);
    const status = get(tracer, "flow.process.processStatuses", []).reduce(
      (previous, current) =>
        Number(previous?.order) < Number(current.order) ? previous : current,
    );
    addProject({
      variables: {
        data: {
          visible: false,
          id: cuid(),
          statuses: [{ id: cuid(), status: { id: get(status, "id") } }],
          author: { id: user.id },
          hasResponsible: true,
          tracer: { id: get(tracer, "id") },
          fields: { processTemplate: get(tracer, "flow.process.theme") },
        },
      },
    });
  };

  const onAssign = ({ projectId, projectFields = {}, authorId }) => {
    setIsAssigning({ [projectId]: true });
    lightUpdateProject({
      variables: {
        where: { id: projectId },
        data: {
          author: { id: authorId },
          fields: { ...projectFields, isManualAssign: true },
        },
      },
      onCompleted: () => setTimeout(() => setIsAssigning({}), 1000),
    });
  };

  const onChangeStatus = ({ project: projectData, status }) => {
    const project = omitDeep(projectData, "__typename");
    const actionsToExecute = filterByConditions(status?.actions || [], project);
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({ id: cuid(), status: { id: status } }));
    const noSyncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.NO_SYNC_PROCESSES)
      .map(({ args: { process } }) =>
        get(project, "statuses", []).find(
          ({ status }) => get(status, "process.id") === process,
        ),
      );
    const statuses = [
      {
        id: cuid(),
        status: { id: status.id },
      },
      ...syncActions,
      ...noSyncActions,
    ];

    updateProject({
      variables: {
        where: { id: project?.id },
        data: { statuses },
      },
      onCompleted: () => setTimeout(() => setIsAssigning({}), 1000),
    });
  };

  if (loading) return <Loading />;

  const filterWithAdvanced = {
    ...filter,
    ...(advancedFilterHook.advancedFiltersVariable
      ? { advancedFilters: advancedFilterHook.advancedFiltersVariable }
      : {}),
  };

  return (
    <View
      filter={filterWithAdvanced}
      matchStatusesWithProcess={matchStatusesWithProcess}
      onDetail={onDetail}
      pagination={pagination}
      setPagination={setPagination}
      onSearch={onSearch}
      applyFilter={applyFilter}
      duplicate={duplicateProject}
      createProject={createProject}
      onDelete={onDelete}
      actions={actions}
      exportProjects={() => setShowExportModal(true)}
      exportLoading={exportLoading}
      sendByEmailLoading={sendByEmailLoading}
      showExportModal={showExportModal}
      setShowExportModal={setShowExportModal}
      handleExportProjects={handleExportProjects}
      handleSendProjectsByEmail={handleSendProjectsByEmail}
      onAssign={onAssign}
      isAssigning={isAssigning}
      processStatuses={get(processStatuses, "processStatuses.data", [])}
      onChangeStatus={onChangeStatus}
      tracers={tracers}
      setTracers={setTracers}
      setTracer={setTracer}
      user={user}
      advancedFilterProps={advancedFilterHook}
    />
  );
};

export default List;
